import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bountyService, uploadService } from '../api/services';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { usePermissions } from '../context/PermissionsContext';
import { getDisplayName } from '../utils/helpers';
import BlockEditor from '../components/BlockEditor';
import FileUpload from '../components/FileUpload';
import BountyPeoplePicker from '../components/BountyPeoplePicker';
import { parseContent } from '../components/BlockRenderer';
import { BOUNTY_TYPES, LEVELS } from '../utils/bountyLabels';
import './Bounty.css';

// 懸賞建立/編輯共用表單。有 :id 參數時為編輯模式。
const BountyFormPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings } = useSettings();
  const { hasPermission, loading: permLoading } = usePermissions();
  const pointsEnabled = settings.bounty_points_enabled === 'true';

  const [form, setForm] = useState({
    bounty_type: 'TASK',
    title: '',
    level: 'MID',
    deadline: '',
    estimated_hours: '',
    points: '',
    address: '',
    contact: '',
  });
  const [contentBlocks, setContentBlocks] = useState([{ type: 'text', content: '' }]);
  const [remarkBlocks, setRemarkBlocks] = useState([{ type: 'text', content: '' }]);
  // 附件清單（與貼文共用 FileUpload 元件）；編輯模式會載入既有附件
  const [attachments, setAttachments] = useState([]);
  // 提出人預設為登入者本人；任務負責人為選填
  const [proposer, setProposer] = useState({ id: user?.id, name: getDisplayName(user) });
  const [assignee, setAssignee] = useState({ id: null, name: '' });
  const [proposerPickerOpen, setProposerPickerOpen] = useState(false);
  const [assigneePickerOpen, setAssigneePickerOpen] = useState(false);

  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    bountyService.get(id)
      .then((res) => {
        if (cancelled) return;
        const b = res.data;
        setForm({
          bounty_type: b.bounty_type || 'TASK',
          title: b.title || '',
          level: b.level || 'MID',
          deadline: b.deadline || '',
          estimated_hours: b.estimated_hours != null ? String(b.estimated_hours) : '',
          points: b.points != null ? String(b.points) : '',
          address: b.address || '',
          contact: b.contact || '',
        });
        setContentBlocks(parseContent(b.content));
        setRemarkBlocks(b.remark ? parseContent(b.remark) : [{ type: 'text', content: '' }]);
        if (b.proposer_id) setProposer({ id: b.proposer_id, name: b.proposer_name || '' });
        if (b.assignee_id) setAssignee({ id: b.assignee_id, name: b.assignee_name || '' });
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    // 載入既有附件，供編輯時檢視與追加（url 補成絕對路徑供預覽）
    uploadService.listByBounty(id)
      .then((res) => {
        if (cancelled) return;
        setAttachments((res.data || []).map((a) => ({ ...a, url: uploadService.getFileUrl(`/api/uploads/file/${a.file_path}`) })));
      })
      .catch(() => { /* 附件載入失敗不阻斷編輯 */ });
    return () => { cancelled = true; };
  }, [id, isEdit]);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // 區塊是否有實質內容
  const blocksHaveContent = (blocks) => blocks.some((b) => (b.content && b.content.trim()) || b.type === 'image');
  // remark 區塊若為空則不送（存空字串）
  const serializeRemark = () => (blocksHaveContent(remarkBlocks) ? JSON.stringify(remarkBlocks) : '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.title.trim()) { setError('請填寫名稱'); return; }
    if (!blocksHaveContent(contentBlocks)) { setError('請填寫問題描述 / 任務範圍'); return; }
    if (!form.deadline) { setError('請選擇截止時間'); return; }
    const hours = Number(form.estimated_hours);
    if (!Number.isFinite(hours) || hours <= 0) { setError('請填寫有效的時數'); return; }
    let points = 0;
    if (pointsEnabled) {
      points = Number(form.points);
      if (!Number.isInteger(points) || points < 0) { setError('請填寫有效的點數'); return; }
    }

    const payload = {
      bounty_type: form.bounty_type,
      title: form.title.trim(),
      level: form.level,
      deadline: form.deadline,
      estimated_hours: hours,
      points,
      address: form.address.trim(),
      contact: form.contact.trim(),
      content: JSON.stringify(contentBlocks),
      remark: serializeRemark(),
      proposer_id: proposer.id || user?.id,
      assignee_id: assignee.id || null,
    };

    setSubmitting(true);
    try {
      if (isEdit) {
        await bountyService.update(id, payload);
        // 將尚未關聯的新附件關聯到此懸賞（已關聯者 bounty_id 已存在，不重複處理）
        await Promise.all(attachments.filter((a) => !a.bounty_id).map((a) => uploadService.linkToBounty(a.id, id)));
        navigate(`/bounties/${id}`);
      } else {
        const res = await bountyService.create(payload);
        // 建立成功後，把所有已上傳附件關聯到新懸賞
        await Promise.all(attachments.map((a) => uploadService.linkToBounty(a.id, res.data.id)));
        navigate(`/bounties/${res.data.id}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (permLoading || loading) return <div className="container mt-5">載入中...</div>;
  // 建立模式需具備開立權限；編輯模式的權限由後端驗證
  if (!isEdit && !hasPermission('bounty.create')) {
    return <div className="container mt-5"><div className="alert alert-danger">你沒有開立懸賞的權限，請聯絡管理員。</div></div>;
  }

  return (
    <div className="bounty-form-page">
      <div className="container">
        <h1>{isEdit ? '編輯懸賞' : '開立懸賞'}</h1>
        {error && <div className="alert alert-danger">{error}</div>}

        <form onSubmit={handleSubmit} className="bounty-form">
          <div className="form-row">
            <div className="form-group">
              <label><span className="required-label">* 分類</span></label>
              <select name="bounty_type" className="form-control" value={form.bounty_type} onChange={change} disabled={submitting}>
                {BOUNTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label><span className="required-label">* 等級</span></label>
              <select name="level" className="form-control" value={form.level} onChange={change} disabled={submitting}>
                {LEVELS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label><span className="required-label">* 名稱</span></label>
            <input type="text" name="title" className="form-control" value={form.title} onChange={change} placeholder="請輸入懸賞名稱" disabled={submitting} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label><span className="required-label">* 截止時間</span></label>
              <input type="date" name="deadline" className="form-control" value={form.deadline} onChange={change} disabled={submitting} />
            </div>
            <div className="form-group">
              <label><span className="required-label">* 時數（預估工時）</span></label>
              <input type="number" name="estimated_hours" className="form-control" value={form.estimated_hours} onChange={change} min="0" step="0.5" placeholder="例如 8" disabled={submitting} />
            </div>
            {pointsEnabled && (
              <div className="form-group">
                <label><span className="required-label">* 點數</span></label>
                <input type="number" name="points" className="form-control" value={form.points} onChange={change} min="0" step="1" placeholder="例如 50" disabled={submitting} />
              </div>
            )}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>提出人</label>
              <div className="picker-field">
                <span className="picker-value">{proposer.name || '（未設定）'}</span>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setProposerPickerOpen(true)} disabled={submitting}>選擇</button>
                {proposer.id !== user?.id && (
                  <button type="button" className="btn btn-sm btn-link" onClick={() => setProposer({ id: user?.id, name: getDisplayName(user) })} disabled={submitting}>改回我</button>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>任務負責人（選填，預先指派）</label>
              <div className="picker-field">
                <span className="picker-value">{assignee.name || '（未指派，開放領取）'}</span>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setAssigneePickerOpen(true)} disabled={submitting}>選擇</button>
                {assignee.id && (
                  <button type="button" className="btn btn-sm btn-link" onClick={() => setAssignee({ id: null, name: '' })} disabled={submitting}>清除</button>
                )}
              </div>
            </div>
          </div>

          <div className="form-group">
            <label><span className="required-label">* 問題描述 / 任務範圍</span></label>
            <BlockEditor blocks={contentBlocks} onChange={setContentBlocks} />
          </div>

          <div className="form-group">
            <label>備註描述（選填）</label>
            <BlockEditor blocks={remarkBlocks} onChange={setRemarkBlocks} />
          </div>

          <div className="form-group">
            <label>附件上傳（選填）</label>
            <FileUpload attachments={attachments} onAttachmentsChange={setAttachments} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>地址（選填）</label>
              <input type="text" name="address" className="form-control" value={form.address} onChange={change} disabled={submitting} />
            </div>
            <div className="form-group">
              <label>聯絡窗口（選填）</label>
              <input type="text" name="contact" className="form-control" value={form.contact} onChange={change} disabled={submitting} />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? '送出中...' : (isEdit ? '更新懸賞' : '開立懸賞')}</button>
            <button type="button" className="btn btn-secondary" onClick={() => navigate(isEdit ? `/bounties/${id}` : '/bounties')} disabled={submitting}>取消</button>
          </div>
        </form>
      </div>

      <BountyPeoplePicker
        open={proposerPickerOpen}
        mode="single"
        title="選擇提出人"
        confirmLabel="確定"
        onClose={() => setProposerPickerOpen(false)}
        onConfirm={(users) => { setProposer({ id: users[0].id, name: users[0].display_name || users[0].name }); setProposerPickerOpen(false); }}
      />
      <BountyPeoplePicker
        open={assigneePickerOpen}
        mode="single"
        title="指派任務負責人"
        confirmLabel="確定"
        onClose={() => setAssigneePickerOpen(false)}
        onConfirm={(users) => { setAssignee({ id: users[0].id, name: users[0].display_name || users[0].name }); setAssigneePickerOpen(false); }}
      />
    </div>
  );
};

export default BountyFormPage;
